import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppDialog } from '../../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { GridSearch } from '../../../components/GridSearch';
import { useConfirm } from '../../../components/confirm';
import { useNotify } from '../../../components/notifications';
import { fmtLocalDate } from '../../../utils/datetime';
import { createPlmDailyQc, fetchPlmDailyQcSlides, type PlmDailyQcSlide } from './plm.api';
import { SOP_RECENT_DAYS, daysAgoText, filterSlides, refusalText } from './plmDailyQc';

/**
 * The "Reference Slide" window behind PLM Daily QC › New — G5 `PlmV2Ui.PickSlide`
 * (PlmV2DailySlide_Pick_ListView): a popup over the list with one row per live slide
 * (Reference ID · Original Source ID · Source · Matrix · Material · Types · Last Run ·
 * Days Ago). Clicking a row picks it; ticking one and pressing OK does the same. The read
 * is then created and handed to `onCreated`, which opens it (G5 `OpenDailyQc`).
 * A slide run within the SOP window (7 days) is flagged ⚠ and asks first — allowed, never blocked.
 */
export function PlmDailySlidePickerDialog({ open, onClose, onCreated }: {
  open: boolean;
  onClose: () => void;
  onCreated: (created: { oid: string; dailyQcId: string | null }) => void;
}) {
  const confirm = useConfirm().confirm;
  const notify = useNotify();
  const [slides, setSlides] = useState<PlmDailyQcSlide[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  // Every opening starts clean, on the current slide list.
  useEffect(() => {
    if (!open) return;
    let live = true;
    setSearch('');
    setSelected(new Set());
    setLoading(true);
    fetchPlmDailyQcSlides()
      .then(s => { if (live) setSlides(s); })
      .catch(e => { if (live) notify.error(refusalText(e, 'The reference slides could not be loaded. Try again.')); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [open, notify]);

  const rows = useMemo(() => filterSlides(slides, search), [slides, search]);
  const ticked = slides.find(s => selected.has(s.oid));

  const pick = useCallback(async (s: PlmDailyQcSlide) => {
    if (busy) return;
    const slide = `${s.referenceId ?? ''}${s.originalSourceId ? ` (${s.originalSourceId})` : ''}`;
    const ok = s.recent
      ? await confirm({
        title: 'Recently used slide',
        message: `${slide} was last run ${daysAgoText(s.daysAgo)}. The SOP prefers a slide not used within `
          + `${SOP_RECENT_DAYS} days. Use it anyway?`,
        confirmLabel: 'Use this slide', cancelLabel: 'Choose another',
      })
      : await confirm({
        title: 'Start Daily QC',
        message: `Read reference slide ${slide} now?`
          + ((s.types ?? 1) > 1 ? ` It holds ${s.types} asbestos types — the read is checked against all of them.` : ''),
        confirmLabel: 'Start',
      });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await createPlmDailyQc(s.oid, null);
      notify.success(`${res.dailyQcId ?? 'Daily QC'} created on ${s.referenceId ?? 'the slide'}.`);
      onCreated(res);
    } catch (e) {
      notify.error(refusalText(e, 'The Daily QC could not be started. Try Refresh, and if it keeps happening report it.'));
    } finally {
      setBusy(false);
    }
  }, [busy, confirm, notify, onCreated]);

  const columns = useMemo<AppColumn<PlmDailyQcSlide>[]>(() => [
    { field: 'referenceId', header: 'Reference ID', width: 110, text: s => s.referenceId ?? '' },
    { field: 'originalSourceId', header: 'Original Source ID', width: 140, text: s => s.originalSourceId ?? '' },
    { field: 'source', header: 'Source', width: 130, text: s => s.source ?? '' },
    { field: 'matrix', header: 'Matrix', width: 80, text: s => s.matrix ?? '' },
    { field: 'material', header: 'Material', width: 100, text: s => s.material ?? '' },
    { field: 'types', header: 'Types', width: 64, align: 'right', text: s => String(s.types ?? 1) },
    { field: 'lastRun', header: 'Last Run', width: 100, text: s => (s.lastRun ? fmtLocalDate(s.lastRun) : 'Never') },
    {
      field: 'daysAgo', header: 'Days Ago', width: 90, align: 'right',
      text: s => (s.daysAgo == null ? '' : String(Math.max(0, s.daysAgo))),
      render: s => (
        <span className="plm-dsp-days">
          {s.recent && (
            <i className="ti ti-alert-triangle plm-dsp-warn"
              title={`Used within ${SOP_RECENT_DAYS} days — allowed, but the SOP prefers another slide`} />
          )}
          {s.daysAgo == null ? '—' : Math.max(0, s.daysAgo)}
        </span>
      ),
    },
  ], []);

  return (
    <AppDialog
      open={open}
      title="Reference Slide"
      icon="ti-microscope"
      width={1100}
      className="dlg-center plm-dsp"
      maximizable
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ca-btn ca-btn-solid" disabled={!ticked || busy}
            onClick={() => { if (ticked) void pick(ticked); }}>
            OK
          </button>
          <button type="button" className="ca-btn" onClick={onClose} disabled={busy}>Cancel</button>
        </>
      }
    >
      <div className="plm-dsp-head">
        <p className="plm-dsp-hint">
          Pick the reference slide you will read today. A slide holding several asbestos types is read for all
          of them; one run within the last {SOP_RECENT_DAYS} days is flagged
          <i className="ti ti-alert-triangle plm-dsp-warn" aria-hidden="true" /> — allowed, but the SOP prefers another.
        </p>
        <GridSearch value={search} onChange={setSearch} placeholder="Search slides…" />
      </div>
      <AppDataGrid<PlmDailyQcSlide>
        rows={rows}
        columns={columns}
        getRowId={s => s.oid}
        loading={loading}
        defaultPageSize={10}
        initialSort={{ key: 'referenceId', dir: 'asc' }}
        selectable
        selectedIds={selected}
        onSelectionChange={next => {
          // One slide at a time: the newest tick wins (G5 "Select one reference slide.").
          const added = [...next].find(id => !selected.has(id));
          setSelected(added != null ? new Set([added]) : new Set(next));
        }}
        onRowClick={s => { void pick(s); }}
        emptyText="No reference slide can be read — add an active slide (Source containing “Slide”) in Settings → Analysis → Reference Library."
      />
    </AppDialog>
  );
}
